import store from '@/store'
import storage from './storage'

let cachedSolutionId = 1

function fetchSolutionsConfig() {
  return window.config?.solutions || []
}
function getApiUrlProdConfig() {
  return window.config?.apiUrlProd || ''
}
function getAuthUrlConfig() {
  return window.config?.authUrl || ''
}
function getSignalrUrlConfig() {
  return window.config?.signalrUrl || ''
}
function getSendbirdTokenConfig() {
  return window.config?.sendbirdToken || ''
}
function getChannelTypeConfig() {
  return window.config?.channelType || 'ServiceRequest'
}
function getConnectUrlConfig() {
  return window.config?.connectUrl || ''
}
function getConnectTitleConfig() {
  return window.config?.connectTitle || 'Congratulations!'
}
function getConnectMessageConfig() {
  return window.config?.connectMessage || 'You are now able to use the new Repairify Connect Platform.'
}
function getConnectSupportMessageConfig() {
  return window.config?.connectSupportMessage || 'If you experience any issues please contact our customer support team on 01923 882392'
}

// const solutionsConfig = window.config?.solutions || []
// const apiUrlProd = window.config?.apiUrlProd || ''
// const authUrlConfig = window.config?.authUrl || ''
// const signalrUrlConfig = window.config?.signalrUrl || ''
// const sendbirdTokenConfig = window.config?.sendbirdToken || ''
// const channelTypeConfig = window.config?.channelType || 'ServiceRequest'

export const imageExtensions = ['jpg', 'jpeg', 'gif', 'png', 'bmp']
export const videoExtensions = ['mp4', 'webm', 'ogg']
export const pdfExtensions = ['pdf']
export const wordDocumentExtensions = ['doc', 'docx']
export const otherDocumentExtensions = ['txt', 'rtf']
export const documentExtensions = [...pdfExtensions, ...wordDocumentExtensions, ...otherDocumentExtensions]
export const allFileExtensions = [...imageExtensions, ...videoExtensions, ...documentExtensions]

export function toBool(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1 || v === -1; // treat 1/-1 as true, 0 as false
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '') return false;
  }
  return !!v; // fallback
}

// Get current solution ID from local storage or default to 1
export function getCurrentSolutionId() {
  return cachedSolutionId
}

export async function initGlobals() {
  const stored = await storage.local.getItem('auth/solution')
  if (stored) {
    cachedSolutionId = parseInt(stored)
  }
}

export function solutionsConfig() {
  return fetchSolutionsConfig()
}

// Find solution config by solution ID
export function getSolutionById(id = getCurrentSolutionId()) {
  const parsedId = parseInt(id)
  const allSolutions = solutionsConfig()

  if (!Array.isArray(allSolutions)) {
    console.error('getSolutionById: solutionsConfig is not an array!', allSolutions)
    return null
  }

  if (allSolutions.length === 1) {
    return allSolutions[0]
  }

  const match = allSolutions.find(item => parseInt(item.id) === parsedId)
  return match || null
}

// UUID Generator
export function generateUUID() {
  let d = new Date().getTime()
  let d2 = (performance?.now?.() || 0) * 1000
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16
    if (d > 0) {
      r = (d + r) % 16 | 0
      d = Math.floor(d / 16)
    } else {
      r = (d2 + r) % 16 | 0
      d2 = Math.floor(d2 / 16)
    }
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

export function apiUrl() {
  const solution = getSolutionById()
  return solution?.url || getApiUrlProdConfig()
}

export function authUrl() {
  const solution = getSolutionById()
  let base = solution?.auth || getAuthUrlConfig()

  // Remove any trailing `/gettoken` or slash
  base = base.replace(/\/gettoken\/?$/i, '').replace(/\/+$/, '')
  return base
}

export function signalrUrl() {
  const solution = getSolutionById()
  return solution?.signalrUrl || getSignalrUrlConfig()
}

export function sendbirdToken() {
  const solution = getSolutionById()
  return solution?.sendbirdToken || getSendbirdTokenConfig()
}

export function channelType() {
  const solution = getSolutionById()
  return solution?.channelType || getChannelTypeConfig()
}

export function connectUrl() {
  return getConnectUrlConfig()
}

export function connectTitle() {
  return getConnectTitleConfig()
}

export function connectMessage() {
  return getConnectMessageConfig()
}

export function connectSupportMessage() {
  return getConnectSupportMessageConfig()
}

// LEG-9814: businesses with UseConnect set (UK stack only) must be routed to
// the Connect page instead of the calibration portal. Single source of truth
// for the gate condition — checked both at login and on every navigation.
export function shouldRedirectToConnect(location) {
  return isUK() && !isMahle() && !isClever() && !isStGobain() && !isAustralia() && location?.useConnect === true
}

// Get App ID depending on solution config
export function appId() {
  const solution = getSolutionById()
  return solution?.appId || 1
}

// Get App Name depending on solution config
// Returns a translation key if Mahle, else string.
export function appName() {
  const solution = getSolutionById()
  if (isMahle()) {
    return 'mahle.label.portaltitle' // translation key
  }
  return solution?.appName || ''
}

// Get Deployment Name depending on solution config
export function name() {
  const solution = getSolutionById()
  return solution?.name || ''
}

// Is UK deployment (appId=1)
export function isUK() {
  return appId() === 1
}

// Is Mahle deployment (UK + hostname includes 'remotepro')
export function isMahle() {
  // return true;
  return isUK() && window.location.hostname.toLowerCase().includes('remotepro')
}

// Is Clever deployment (solution ID=11 or hostname contains clever/clportal)
export function isClever() {
  const solutionId = getCurrentSolutionId()
  if (solutionId === 11) return true
  const host = window.location.hostname.toLowerCase()
  return host.includes('clever') || host.includes('clportal')
}

export function isAustralia() {
  return name() === 'Australia';
}

export function isStGobain() {
  return parseInt(store.getters['auth/solution']) === 10;
}

export function isRepairify() {
  let isRepairify = window.location.hostname.toLowerCase().includes('repairify');
  if (!isRepairify) {
    isRepairify = name() === 'Australia';
  }
  if (!isRepairify) {
    isRepairify = window.location.hostname.toLowerCase().includes('auportal');
  }
  return isRepairify;
}

export function isLaunch() {
  return window.location.hostname.toLowerCase().includes('launch')
}

export function isUKPortal3() {
  const hostname = window.location.hostname.toLowerCase()
  return hostname.includes('uk') && hostname.includes('portal3')
}

export function isGlass() {
  return window.location.hostname.toLowerCase().includes('glass')
}

// Get logo path depending on solution config and hostname
export function logoPath() {
  const solution = getSolutionById()
  let logoPath = '/static/logo.png' // default fallback

  if (solution?.domainLogo?.length) {
    for (const logoItem of solution.domainLogo) {
      if (logoItem.domainName.includes(window.location.hostname)) {
        logoPath = logoItem.logoPath
        break
      }
    }
  }

  return logoPath
}

export function lookupUrl(solutionId) {
  const allSolutions = solutionsConfig()
  const match = allSolutions.find(item => parseInt(item.id) === parseInt(solutionId))
  return match?.url || getApiUrlProdConfig()
}

// Async: Get dynamic config boolean from local storage CONFIGURATION
export async function getDynamicConfigurationValue(lookupKey) {
  const cached = await storage.local.getItem(lookupKey)
  if (cached !== null && cached !== undefined) {
    return String(cached) === '1'
  }

  const configGroupRaw = await storage.local.getItem('CONFIGURATION')
  if (!configGroupRaw) return false

  try {
    let configArray = JSON.parse(configGroupRaw)

    // If it's a string, parse again
    if (typeof configArray === 'string') {
      configArray = JSON.parse(configArray)
    }

    if (Array.isArray(configArray)) {
      const item = configArray.find(i => i.text === lookupKey)
      const val = item?.value ?? '0'
      await storage.local.setItem(lookupKey, String(val))
      return String(val) === '1'
    } else {
      console.error('ERROR => configArray is NOT an array. Type:', typeof configArray)
    }
  } catch (e) {
    console.error('ERROR => Failed parsing config JSON', e)
  }
  return false
}

// Some boolean flags with async
export async function isRagEnabled() {
  return !isMahle() && (await getDynamicConfigurationValue('RagRatingEnabled'))
}
export async function isMahleRagEnabled() {
  return isMahle() && (await getDynamicConfigurationValue('MAHLERagRatingEnabled'))
}
export async function isPlatformRagEnabledFromConfig() {
  return await isRagEnabled() || await isMahleRagEnabled()
}
export async function isOpeningHoursEnabled() {
  return !isMahle() && (await getDynamicConfigurationValue('OpeningHoursEnabled'))
}
export async function isMahleOpeningHoursEnabled() {
  return isMahle() && (await getDynamicConfigurationValue('MAHLEOpeningHoursEnabled'))
}
export async function isChatFileUploadEnabled() {
  return await getDynamicConfigurationValue('ChatFileUploadEnabled')
}
export async function isSendbirdAuditingEnabled() {
  return await getDynamicConfigurationValue('SendbirdAuditEnabled')
}

export function clientSurveyConfigKey() {
  return isMahle() ? 'MAHLEClientSurveyEnabled' : 'ClientSurveyEnabled'
}
export async function isClientSurveyEnabledForThisTenant() {
  const key = clientSurveyConfigKey()
  return !!(await getDynamicConfigurationValue(key))
}
// Async getters/setters for session storage keys
export async function getCurrentPage() {
  return await storage.session.getItem('currentPage')
}

export async function setCurrentPage(value) {
  await storage.session.setItem('currentPage', value)
}

export async function getUsername() {
  return await storage.session.getItem('username')
}

export async function setUsername(value) {
  await storage.session.setItem('username', value)
}

export async function getEmail() {
  return await storage.session.getItem('email')
}

export async function setEmail(value) {
  await storage.session.setItem('email', value)
}

export async function getIsUserProfileComplete() {
  return await storage.session.getItem('isUserProfileComplete')
}

export async function setIsUserProfileComplete(value) {
  await storage.session.setItem('isUserProfileComplete', value)
}

// Helper: format date/time culturally
// Can be moved to a separate dateUtils file if preferred
export function formatDateTimeCultural(date) {
  if (!date) return null
  let language = store.getters['auth/language']
  if (language) {
    language = language + '-' + language.toUpperCase()
    return date.toLocaleString(language)
  }
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

export function formatDateTime(date) {
  if (date !== undefined && date !== null) {
    let language = store.getters['auth/language']
    if (language !== '') {
      language += '-' + language.toUpperCase()
      return date.toLocaleString(language)
    } else {
      let hours = date.getHours()
      let minutes = date.getMinutes()
      const ampm = hours >= 12 ? 'pm' : 'am'
      hours = hours % 12
      hours = hours || 12 // the hour '0' should be '12'
      minutes = minutes < 10 ? '0' + minutes : minutes
      const strTime = hours + ':' + minutes + ' ' + ampm
      return date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear() + ' ' + strTime
    }
  } else {
    return null
  }
}

export function formatDateTimeLong(date) {
  if (date !== undefined && date !== null) {
    let hours = date.getHours()
    let minutes = date.getMinutes()
    hours = hours < 12 ? '0' + hours : hours
    minutes = minutes < 10 ? '0' + minutes : minutes
    const strTime = 'T' + hours + ':' + minutes
    let month = date.getMonth() + 1
    month = month < 10 ? '0' + month : month
    let day = date.getDate()
    day = day < 10 ? '0' + day : day
    return date.getFullYear() + '-' + month + '-' + day + strTime
  } else {
    return null
  }
}

export function defaultLanguage() {
  const solutionId = getCurrentSolutionId()
  let defaultLang = window.config ? window.config.language : 'en'
  const allSolutions = solutionsConfig() // Call the exported function

  if (allSolutions.length === 1) {
    if (allSolutions[0].defaultLanguage) {
      defaultLang = allSolutions[0].defaultLanguage
    }
  } else {
    allSolutions.forEach(item => {
      if (parseInt(item.id) === parseInt(solutionId)) {
        if (item.defaultLanguage) {
          defaultLang = item.defaultLanguage
        }
      }
    })
  }

  console.log('defaultLanguage : ', defaultLang)
  return defaultLang
}

export function sortArray(data) {
  const uniqueSet = new Set(data)
  const uniqueArray = Array.from(uniqueSet)
  return uniqueArray.sort((a, b) => {
    if (a[0] === b[0]) {
      const x = a['name']
      const y = b['name']
      return x < y ? -1 : x > y ? 1 : 0
    }
    return a[0] - b[0]
  })
}

export function jsonToQueryString(json) {
  if (!json) return ''
  return (
    '?' +
    Object.keys(json)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(json[key]))
      .join('&')
  )
}

export function getCultureCode(code) {
  switch (code) {
    case 'en':
    case 'en-EN': // United States
      return 'en-US'
    case 'el':
    case 'gr':
    case 'el-EL': // Greek
      return 'el-GR'
    case 'sv': // Swedish
      return 'sv-SE'
    case 'uk': // Ukraine
      return 'uk-UA'
    case 'sl': // Slovenia
      return 'sl-SI'
    case 'sr': // Serbia
      return 'sr-RS'
    case 'no': // Norwegian
    case 'nb':
      return 'nb-NO'
    case 'et': // Estonian
      return 'et-EE'
    case 'bs': // Bosnian
      return 'bs-BA'
    case 'be': // Belarusian
      return 'be-BY'
    case 'da': // Danish
      return 'da-DK'
    case 'sq': // Albania
      return 'sq-AL'
    default:
      return code + '-' + code.toUpperCase()
  }
}

export default {
  generateUUID,
  lookupUrl,
  apiUrl,
  authUrl,
  signalrUrl,
  sendbirdToken,
  channelType,
  connectUrl,
  connectTitle,
  connectMessage,
  connectSupportMessage,
  shouldRedirectToConnect,
  appId,
  isUK,
  isAustralia,
  isStGobain,
  isRepairify,
  isClever,
  getCurrentPage,
  setCurrentPage,
  getUsername,
  setUsername,
  getEmail,
  setEmail,
  getIsUserProfileComplete,
  setIsUserProfileComplete,
  name,
  appName,
  isMahle,
  isPlatformRagEnabledFromConfig,
  isOpeningHoursEnabled,
  isMahleOpeningHoursEnabled,
  isChatFileUploadEnabled,
  isSendbirdAuditingEnabled,
  isClientSurveyEnabledForThisTenant,
  isLaunch,
  isUKPortal3,
  isGlass,
  logoPath,
  defaultLanguage,
  sortArray,
  jsonToQueryString,
  getCultureCode,
  imageExtensions,
  videoExtensions,
  pdfExtensions,
  wordDocumentExtensions,
  otherDocumentExtensions,
  documentExtensions,
  allFileExtensions,
  toBool
}
